import React, {useEffect, useMemo, useState} from "react";

import {DishDetailsItem} from "@apps/dish/interfaces";
import * as Sentry from "@sentry/react";
import cx from "classnames";
import moment from "moment";

import {EventOrigin, recordConversionEvent} from "@globals/config/eventUtils";
import {useUser} from "@globals/user";
import formatCurrency from "@globals/utils/formatCurrency";

import Button, {ButtonAccent} from "@components/Button";
import {useDishDetailsConfig} from "@components/DishDetails";
import LoadingDots from "@components/LoadingDots";
import NumberInput from "@components/forms/inputs/NumberInput";
import {ModalTrigger} from "@components/modals";

import styles from "./ItemPurchaseButton.scss";

export type ItemPurchaseButtonProps = {
  isBigButton?: boolean;
  wrapperClassName?: string;
  item: DishDetailsItem;
  plusMinusClassName?: string;
  postAddOneToCartCallback?: () => void;
};

type ButtonWrapperProps = {
  confirmationModal?: React.ElementType;
  children: JSX.Element;
  dishItem: DishDetailsItem;
};

const ButtonWrapper = ({
  confirmationModal,
  children,
  dishItem,
}: ButtonWrapperProps) => {
  if (confirmationModal) {
    return (
      <ModalTrigger dishItem={dishItem} modalComponent={confirmationModal}>
        {children}
      </ModalTrigger>
    );
  }
  return children;
};

export default function ItemPurchaseButton({
  isBigButton = false,
  wrapperClassName,
  item,
  plusMinusClassName,
  postAddOneToCartCallback,
}: ItemPurchaseButtonProps): JSX.Element {
  const {user} = useUser();
  const {
    purchaseConfirmationModal,
    addButtonText,
    orderItems,
    showQuantityInput,
    changeItemQty,
    isPriceShown,
    selectedDate,
    showPriceInPurchaseButton,
    menuType,
  } = useDishDetailsConfig();

  const isMultiLocation = (user?.locations_list?.length ?? 0) > 1;
  const showDeliverTo =
    isMultiLocation && !isBigButton && !showQuantityInput && !item.soldout;
  if (item.food_category === undefined || item.food_category === null) {
    Sentry.captureMessage(
      `Potential ProductLines error: item.food_category is undefined or null for item ${item.id}, menu type: ${menuType}`
    );
  }
  const orderQty =
    orderItems?.find(
      (orderItem) =>
        orderItem.item.item_id === item.item_id &&
        orderItem.product_id === item.food_category?.product_id
    )?.count ?? 0;

  const [itemQty, setItemQty] = useState(orderQty);
  const [isLoading, setIsLoading] = useState(false);

  // update qty if item order qty changes from the cart;
  useEffect(() => {
    if (itemQty !== orderQty) {
      setItemQty(orderQty);
    }
  }, [orderQty]);

  const handleChangeQty = (qty: number) => {
    setItemQty(qty);
    changeItemQty({
      item: item,
      quantity: qty,
      isCheckingAttendees: qty < orderQty,
      productId: item.food_category?.product_id,
    }).catch(() => {
      // fallback to original order qty;
      setItemQty(orderQty);
    });
  };

  const addOneToCart = () => {
    setIsLoading(true);
    changeItemQty({
      item: item,
      quantity: 1,
      deliveryDate: selectedDate,
    })
      .then(() => {
        const origin = isBigButton ? EventOrigin.QUICKVIEW : EventOrigin.PAGE;
        recordConversionEvent(origin, "add_to_cart", item.price, {
          items: [
            {item_id: item.id.toString(), item_name: item.item, quantity: 1},
          ],
        });
      })
      .finally(() => {
        setIsLoading(false);

        if (postAddOneToCartCallback) {
          postAddOneToCartCallback();
        }
      });
  };

  const addButtonTextContent = useMemo(() => {
    if (isBigButton && selectedDate) {
      // in dishDetails modal;
      return `Add to cart for ${moment(selectedDate).format("ddd M/D")}`;
    } else if (showPriceInPurchaseButton && isPriceShown(item)) {
      return formatCurrency(Number(item.price));
    }
    return addButtonText;
  }, [addButtonText, item.price, selectedDate]);

  return (
    <div className={cx(styles.wrapper, wrapperClassName)}>
      {showDeliverTo && (
        <span className={styles.multiLocation}>
          Deliver to{" "}
          {user?.selected_location?.name || user?.selected_location?.address}
        </span>
      )}
      {!item.soldout && !showQuantityInput && (
        <ButtonWrapper
          confirmationModal={purchaseConfirmationModal}
          dishItem={item}
        >
          <Button
            accent={
              isBigButton
                ? ButtonAccent.AccentDefault
                : ButtonAccent.AccentOrange
            }
            onClick={addOneToCart}
            withBorder={!isBigButton}
            className={cx(styles.button, {
              [styles.normalBuyButton]: !isBigButton,
              [styles.bigBuyButton]: isBigButton,
              [styles.multiLocationButton]: isMultiLocation && !isBigButton,
            })}
          >
            {isLoading ? (
              <LoadingDots className={styles.loading} />
            ) : (
              addButtonTextContent
            )}
          </Button>
        </ButtonWrapper>
      )}
      {item.soldout && (
        <span className={cx(styles.soldout, isBigButton && styles.bigSoldout)}>
          Sold Out
        </span>
      )}
      {showQuantityInput && !item.soldout && (
        <NumberInput
          value={itemQty}
          onChange={handleChangeQty}
          className={plusMinusClassName}
        />
      )}
    </div>
  );
}
